import { Component } from '@angular/core';
import moment from 'moment';
import { take } from 'rxjs/operators';
import { SageBuildResult } from '../../models/sage-export.model';
import { SageExportService } from '../../services/sage-export.service';

@Component({
    selector: 'app-sage-export-build',
    templateUrl: './sage-export-build.component.html',
    styleUrls: ['./sage-export-build.component.scss'],
    standalone: false
})
export class SageExportBuildComponent {

  /** Everything invoiced on or before this date that Sage has not confirmed taking. */
  upTo: string = moment().format('YYYY-MM-DD');

  running: boolean = false;

  /**
   * The batch just built, kept in memory so "Download again" never triggers a second build.
   * A second build would produce a second file over the same documents, and Sage does not
   * deduplicate.
   */
  result: SageBuildResult = null;

  /** Every reason the batch was refused, one per line. Persists until the next attempt. */
  faults: string[] = [];

  /**
   * A refusal that is not a fault list - "there is nothing waiting to go to Sage" on a clean
   * month end, or the date validation message. Rendered as information rather than as work.
   */
  notice: string = '';

  copied: boolean = false;

  /**
   * Kept apart from `copied` rather than shared. The two lists are copied from different panels
   * and one of them sits inside a successful build, so a single flag would put "Copied." next to
   * a list nobody touched.
   */
  excludedCopied: boolean = false;

  constructor(private sageExportService: SageExportService) { }

  /**
   * Builds the batch and saves the file.
   *
   * The download happens here rather than behind a second click because the file is the point of
   * the screen; the panel that stays afterwards is the reminder that having it is not the same as
   * Sage having taken it.
   */
  onBuild() {
    this.faults = [];
    this.notice = '';
    this.result = null;
    this.copied = false;
    this.excludedCopied = false;
    this.running = true;

    this.sageExportService.build(this.upTo)
    .pipe(take(1))
    .subscribe({
      next: (data: SageBuildResult) => {
        this.result = data;
        this.running = false;

        this.sageExportService.downloadCsv(data.batchRef, data.csv);
      },
      error: (e: any) => {
        this.running = false;

        // Session problems are the app's to handle and are not something the operator can fix
        // from this list.
        if (e.status === 401 || e.status === 403) {
          return;
        }

        this.onRefused(e);
      }
    })
  }

  /**
   * Splits a refusal into the work list or the single-line notice.
   *
   * The backend returns every fault at once precisely so they can be fixed in one pass, so the
   * screen must show all of them. A single-line message is a different thing and must not be
   * dressed up as a fault list.
   */
  private onRefused(e: any) {
    const faults: string[] = this.sageExportService.faultsFrom(e);

    if (faults.length === 0) {
      this.notice = 'The batch could not be built. Please try again later.';
      return;
    }

    if (faults.length === 1) {
      this.notice = faults[0];
      return;
    }

    this.faults = faults;
  }

  /** Re-saves the file already in memory. Never rebuilds. */
  downloadAgain() {
    if (!this.result) {
      return;
    }

    this.sageExportService.downloadCsv(this.result.batchRef, this.result.csv);
  }

  /**
   * The excluded list, as text somebody can paste into an email.
   *
   * Names the batch, because by the time this is read the operator has a file and the question
   * is always "what is not in it".
   */
  copyExcluded() {
    const lead: string = this.excludedInvoiceIds.length + ' invoice(s) were left out of Sage batch '
      + (this.result ? this.result.batchRef : '') + ' because they carry no lines:';
    const lines: string[] = this.excludedInvoiceIds.map((invoiceId: number) => 'Invoice ' + invoiceId);

    navigator.clipboard.writeText([lead].concat(lines).join('\n')).then(() => {
      this.excludedCopied = true;
    }, () => {
      this.excludedCopied = false;
    });
  }

  /** The fault list usually has to go to whoever owns the accounts. */
  copyFaults() {
    navigator.clipboard.writeText(this.faults.join('\n')).then(() => {
      this.copied = true;
    }, () => {
      this.copied = false;
    });
  }

  /** The lead sentence the backend puts in front of the faults. */
  get faultHeading(): string {
    return this.faults.length > 0 ? this.faults[0] : '';
  }

  get faultLines(): string[] {
    return this.faults.slice(1);
  }

  /**
   * A clean month end reads as information, not as a failure. The backend uses the same 400 for
   * it as for a refusal, so the two are told apart by the text.
   */
  get noticeIsClean(): boolean {
    return this.notice.indexOf('nothing waiting') !== -1;
  }

  /**
   * An unmapped tax code is the one fault with an in-app fix, and its own text tells the operator
   * to set it on the tax type - so the way there belongs next to it.
   */
  get hasTaxCodeFault(): boolean {
    return this.faults.some((fault: string) => fault.indexOf('Sage tax code') !== -1);
  }

  get creditNoteCount(): number {
    return this.result && this.result.creditNoteIds ? this.result.creditNoteIds.length : 0;
  }

  get invoiceCount(): number {
    return this.result && this.result.invoiceIds ? this.result.invoiceIds.length : 0;
  }

  get documentCount(): number {
    return this.invoiceCount + this.creditNoteCount;
  }

  /**
   * Invoices the backend left out of the file because they carry no lines.
   *
   * Guarded the same way as the id lists above, and for the same reason: the service casts the
   * response body rather than constructing a `SageBuildResult`, so the class defaults never run
   * and a response without the field would leave this undefined.
   */
  get excludedInvoiceIds(): number[] {
    return this.result && this.result.excludedInvoiceIds ? this.result.excludedInvoiceIds : [];
  }
}
